import React, { useState, useEffect } from 'react';

import { SaveOutlined, UserOutlined, LockOutlined, BellOutlined, SafetyOutlined, MobileOutlined, EditOutlined } from '@ant-design/icons';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { Avatar, Card, Button, message, Spin, Form, Input, Switch, Tag, Space, Table, Row, Col, Typography, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import operatorApiFetch from '@services/operator/apiService';

import TotpSettings from './TotpSettings';

const { Text } = Typography;

export default function ProfileShow() {
  const { t } = useTranslation();
  const { operator: currentUser } = useOperatorAuth();
  const [basicInfoForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [operator, setOperator] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState([]);
  const [basicInfoOpen, setBasicInfoOpen] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchOperator();
      fetchNotificationPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const fetchOperator = async () => {
    try {
      setLoading(true);
      const response = await operatorApiFetch(`operators/${currentUser.id}`);
      const result = await response.json();

      if (result.success && result.data) {
        setOperator(result.data);
      } else {
        message.error(result.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch operator:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const openBasicInfoModal = () => {
    basicInfoForm.setFieldsValue({
      name: operator.name,
      username: operator.username,
      email: operator.email,
    });
    setBasicInfoOpen(true);
  };

  const handleUpdateBasicInfo = async (values) => {
    try {
      setSaving(true);
      const response = await operatorApiFetch(`operators/${currentUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const result = await response.json();

      if (result.success) {
        message.success(t('operator.profile.success'));
        setBasicInfoOpen(false);
        fetchOperator();
      } else {
        message.error(result.message || t('operator.profile.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to update operator:', error);
      message.error(t('operator.profile.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await operatorApiFetch('notification_subscriptions');
      const result = await response.json();
      if (result.success && result.data?.preferences) {
        setNotificationPreferences(result.data.preferences);
      }
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  };

  const handleToggleNotification = async (notificationType, channel, enabled) => {
    setNotificationPreferences(prev =>
      prev.map(p =>
        p.notification_type === notificationType && p.channel === channel
          ? { ...p, enabled }
          : p
      )
    );

    try {
      const payload = { subscriptions: [{ notification_type: notificationType, channel, enabled }] };
      const response = await operatorApiFetch('notification_subscriptions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.success && result.data?.preferences) {
        setNotificationPreferences(result.data.preferences);
        message.success(t('operator.profile.notifications.update_success'));
      } else {
        setNotificationPreferences(prev =>
          prev.map(p =>
            p.notification_type === notificationType && p.channel === channel
              ? { ...p, enabled: !enabled }
              : p
          )
        );
        message.error(t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to update notification subscription:', error);
      setNotificationPreferences(prev =>
        prev.map(p =>
          p.notification_type === notificationType && p.channel === channel
            ? { ...p, enabled: !enabled }
            : p
        )
      );
      message.error(t('common.error.operation_failed'));
    }
  };

  const handleUpdatePassword = async (values) => {
    try {
      setSavingPassword(true);
      const response = await operatorApiFetch(`operators/${currentUser.id}/update_password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: values.current_password,
          password: values.password,
        }),
      });

      const result = await response.json();

      if (result.success) {
        message.success(t('operator.profile.password_success_redirect'));
        passwordForm.resetFields();
        setTimeout(() => { window.location.href = '/operator/login'; }, 1000);
      } else {
        message.error(result.message || t('operator.profile.error.password_failed'));
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      message.error(t('operator.profile.error.password_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading || !operator) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isDisabled = Boolean(operator.disabled_at);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      {/* Identity Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar size={56} icon={<UserOutlined />} />
          <div style={{ flex: 1 }}>
            <Space size={12} align="center" style={{ marginBottom: 4 }}>
              <Text strong style={{ fontSize: 18 }}>{operator.name || '-'}</Text>
              {isDisabled ? (
                <Tag color="error">{t('operator.operators.edit.account_status.disabled')}</Tag>
              ) : (
                <Tag color="success">{t('operator.operators.edit.account_status.active')}</Tag>
              )}
            </Space>
            <div>
              <Text type="secondary">
                @{operator.username}
                {operator.email ? ` · ${operator.email}` : ''}
              </Text>
            </div>
          </div>
          <Button icon={<EditOutlined />} onClick={openBasicInfoModal}>
            {t('common.button.edit')}
          </Button>
        </div>
      </Card>

      {/* 2-column Grid */}
      <Row gutter={[16, 16]}>
        {/* Left — 資安 */}
        <Col xs={24} lg={12}>
          <Card title={<Space><LockOutlined />{t('operator.profile.change_password')}</Space>} style={{ marginBottom: 16 }}>
            <Form form={passwordForm} layout="vertical" onFinish={handleUpdatePassword}>
              <Form.Item
                label={t('operator.profile.fields.current_password')}
                name="current_password"
                rules={[{ required: true, message: t('operator.profile.validation.current_password_required') }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('operator.profile.placeholders.current_password')} />
              </Form.Item>
              <Form.Item
                label={t('operator.profile.fields.new_password')}
                name="password"
                rules={[
                  { required: true, message: t('operator.profile.validation.password_required') },
                  { min: 6, message: t('operator.profile.validation.password_min_length') },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('operator.profile.placeholders.new_password')} />
              </Form.Item>
              <Form.Item
                label={t('operator.profile.fields.confirm_password')}
                name="confirm_password"
                dependencies={['password']}
                rules={[
                  { required: true, message: t('operator.profile.validation.confirm_password_required') },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      return !v || getFieldValue('password') === v
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('operator.profile.validation.password_mismatch')));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('operator.profile.placeholders.confirm_password')} />
              </Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingPassword}>
                {t('operator.profile.update_password')}
              </Button>
            </Form>
          </Card>

          <Card title={<Space><SafetyOutlined />{t('operator.totp.settings.title')}</Space>}>
            <TotpSettings />
          </Card>
        </Col>

        {/* Right — 裝置 + 通知 */}
        <Col xs={24} lg={12}>
          <Card title={<Space><MobileOutlined />{t('operator.profile.devices.title')}</Space>} style={{ marginBottom: 16 }}>
            {operator.devices?.length > 0 ? (
              <Table
                dataSource={operator.devices}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: t('operator.profile.devices.columns.device_name'),
                    dataIndex: 'device_name',
                    render: (name) => name || '-',
                  },
                  {
                    title: t('operator.profile.devices.columns.platform'),
                    dataIndex: 'platform',
                    render: (platform) => (
                      <Tag color={platform === 'ios' ? 'blue' : 'green'}>{platform?.toUpperCase()}</Tag>
                    ),
                  },
                  {
                    title: t('operator.profile.devices.columns.registered_at'),
                    dataIndex: 'created_at',
                    render: (d) => new Date(d).toLocaleString(),
                  },
                ]}
              />
            ) : (
              <Text type="secondary">{t('operator.profile.devices.empty')}</Text>
            )}
          </Card>

          {notificationPreferences.length > 0 && (
            <Card title={<Space><BellOutlined />{t('operator.profile.notifications.title')}</Space>}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                {t('operator.profile.notifications.description')}
              </Text>
              <div>
                {notificationPreferences.map((pref) => (
                  <div
                    key={`${pref.notification_type}-${pref.channel}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <span>{t(`operator.profile.notifications.types.${pref.notification_type}`)}</span>
                    <Switch
                      checked={pref.enabled}
                      onChange={(checked) => handleToggleNotification(pref.notification_type, pref.channel, checked)}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Edit Basic Info Modal */}
      <Modal
        title={t('operator.profile.basic_info')}
        open={basicInfoOpen}
        onCancel={() => setBasicInfoOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={basicInfoForm} layout="vertical" onFinish={handleUpdateBasicInfo}>
          <Form.Item
            label={t('operator.profile.fields.name')}
            name="name"
            rules={[{ required: true, message: t('operator.profile.validation.name_required') }]}
          >
            <Input placeholder={t('operator.profile.placeholders.name')} />
          </Form.Item>
          <Form.Item
            label={t('operator.profile.fields.username')}
            name="username"
            rules={[{ required: true, message: t('operator.profile.validation.username_required') }]}
          >
            <Input placeholder={t('operator.profile.placeholders.username')} />
          </Form.Item>
          <Form.Item
            label={t('operator.profile.fields.email')}
            name="email"
            rules={[{ type: 'email', message: t('operator.profile.validation.email_invalid') }]}
          >
            <Input type="email" placeholder={t('operator.profile.placeholders.email')} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setBasicInfoOpen(false)}>{t('common.button.cancel')}</Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                {t('common.button.save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
